import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    category: '',
    shape: '',
    finish: ''
  });

  const categories = ['All', 'Home Decor', 'Office', 'Garden'];
  const shapes = ['All', 'Round', 'Squared', 'Cylindrical', 'Oval', 'U shaped'];
  const finishes = ['All', 'Glossy', 'Matte', 'Custom'];

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, products]);

  const fetchProducts = async () => {
    try {
      const response = await api.get('/products');
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = products;

    if (filters.category && filters.category !== 'All') {
      filtered = filtered.filter(p => p.category === filters.category);
    }

    if (filters.shape && filters.shape !== 'All') {
      filtered = filtered.filter(p => p.shape.includes(filters.shape));
    }

    if (filters.finish && filters.finish !== 'All') {
      filtered = filtered.filter(p => p.finish.includes(filters.finish));
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
  };

  return (
    <div data-testid="products-page" className="min-h-screen pt-24 pb-16 bg-[#F4F4F4]">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 data-testid="products-page-heading" className="text-4xl md:text-5xl font-light tracking-tighter mb-4">
            Our Collection
          </h1>
          <p className="text-base text-[#7a5e75]">
            Explore our complete range of FRP planters
          </p>
        </motion.div>

        {/* Filters - compact inline */}
        <div data-testid="product-filters" className="mb-8 flex flex-wrap items-center gap-2">
          {categories.map(cat => (
            <button
              key={cat}
              data-testid={`filter-category-${cat.toLowerCase().replace(' ', '-')}`}
              onClick={() => handleFilterChange('category', cat === 'All' ? '' : cat)}
              className={`px-5 py-2 text-xs uppercase tracking-wider transition-all ${
                (filters.category === cat || (cat === 'All' && !filters.category))
                  ? 'bg-[#641b4d] text-white'
                  : 'bg-white text-[#641b4d] border border-[#E5E2DC] hover:border-[#641b4d]'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loading-spinner" data-testid="loading-spinner" />
          </div>
        ) : (
          <div>
            <p data-testid="products-count" className="text-sm text-[#7a5e75] mb-6">
              Showing {filteredProducts.length} product{filteredProducts.length !== 1 ? 's' : ''}
            </p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6"
            >
              {filteredProducts.map(product => (
                <ProductCard key={product.id} product={product} />
              ))}
            </motion.div>
            {filteredProducts.length === 0 && (
              <div data-testid="no-products-message" className="text-center py-12">
                <p className="text-[#7a5e75]">No products match your filters.</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Products;